import { FilePreview } from '@/components/Form/types'
import fetchFileFromUrl from '@/components/Form/utils/file/fetchFileFromUrl'
import createFileData from '@/components/Form/utils/file/createFileData'

const materialTypeLabels: Record<string, string> = {
	steel: 'Steel / Metal',
	gold: 'Gold',
	sandblast: 'Sandblast / Frosted',
	anodized: 'Anodized',
	wood_burn: 'Wood Burn',
	glass_etch: 'Glass Etch',
	color: 'Direct Color',
	custom: 'Custom (upload pattern)',
}



type SectionOutput = {
	validation: any[];
	formData: {
		height: string;
		name: string;
		offsetY: string;
		radialAngle: string;
		sectionId: string;
		targetObject: any;
		width: number;
		thumbnail?: FilePreview | null;
		centerRadialAngle: number
		engravingSetting: { }

	};
};

const mapSections = async (
	sections: any[],
): Promise<SectionOutput[]> => {

	return Promise.all(
		sections.map(async (s) => {

			const hasThumbnail = s.thumbnail_image_url || s.target_object?.thumbnail_image_url
			let thumbnail = null
			if (hasThumbnail) {
				const file: any = await fetchFileFromUrl(hasThumbnail)
				thumbnail = await createFileData({
					file: file.file,
					fileExtension: file.fileExtension,
					url: s.thumbnail_image_url,
					types: 'image/*',
				})
			}

			// Load the custom material pattern (only present for material_type 'custom'
			// in pattern mode — colour mode stores material_color instead)
			const materialFileUrl = s.target_object?.material_file
			let materialFile = null
			if (materialFileUrl) {
				const file: any = await fetchFileFromUrl(materialFileUrl)
				materialFile = await createFileData({
					file: file.file,
					fileExtension: file.fileExtension,
					url: materialFileUrl,
					types: 'image/*',
				})
			}

			// Load section image from target_object (used for 2D profiles)
			const sectionImageUrl = s.target_object?.view_url || s.target_object?.thumbnail_image_url
			let sectionImage = null
			if (sectionImageUrl) {
				const file: any = await fetchFileFromUrl(sectionImageUrl)
				sectionImage = await createFileData({
					file: file.file,
					fileExtension: file.fileExtension,
					url: sectionImageUrl,
					types: 'image/*',
				})
			}



			return {
				validation: [],
				formData: {

					height: s.default_size.height.toString(),
					width: s.default_size.width.toString(),
					name: s.name,
					offsetX: s.default_location.x?.toString() || '0',
					offsetY: s.default_location.y.toString(),
					radialAngle: s.radial_angle.toString(),
					face: s.radial_angle >= 90
						? { value: 'back', label: 'Back' }
						: { value: 'front', label: 'Front' },
					sectionId: s.id,
					targetObject: s.target_object,

					thumbnail: thumbnail,
					sectionImage: sectionImage,

					// Surfaced as a top-level form value so the placement editor can edit it;
					// it is also still on targetObject, which is what the renderer reads.
					placement: s.target_object?.placement ?? null,

					centerRadialAngle: s.target_object?.center_radial_angle?.toString() || '0',
					mask: s.target_object?.mask || null,

					shapeType: s.target_object?.shape_type || 'rectangle',
					rotation: (s.artwork_angle || 0).toString(),
					radius: s.target_object?.radius?.toString() || null,
					vertices: s.target_object?.vertices || null,

					materialType: s.target_object?.material_type
						? { value: s.target_object.material_type, label: materialTypeLabels[s.target_object.material_type] || s.target_object.material_type }
						: { value: 'steel', label: 'Steel / Metal' },
					// Which mode a saved section used is implied by which field it carries,
					// so no extra flag needs storing.
					materialSource: {
						mode: materialFileUrl ? 'pattern' : 'color',
						color: s.target_object?.material_color || '#c69e4a',
						file: materialFile,
					},
					engravingTint: s.target_object?.engraving_tint ?? 0.5,
					overlayOpacity: s.target_object?.overlay_opacity ?? 1.0,

					graphicCategoryIds: (s.graphic_categories || []).map((c: any) => ({
						value: c.id,
						label: c.name,
					})),

					designTemplateIds: (s.design_templates || []).map((t: any) => ({
						value: t.id,
						label: t.name,
					})),

					engravingSetting: {
						maintain_aspect_ratio: s.maintain_aspect_ratio,
						preview_mark_speed: s.preview_mark_speed?.toString() || '0',
						marking_order: {value: s.marking_order},
						horizontal_ratio_percentage: s.keystone_correction.horizontal_ratio_percentage?.toString(),
						vertical_ratio_percentage: s.keystone_correction.vertical_ratio_percentage.toString(),
						preview_mark_type: { value: s.preview_mark_type},
						outline_laser_profile_id: { value: s.outline_laser_profile.id},
						hatch_profile_ids: s.hatch_profiles?.map((hatchId: any) => ({
							value: hatchId.id
						})) || [],
						center_section_to_field: s.center_section_to_field
					}
				},
			}
		}),
	)
}

export default mapSections